import type { SpringPage } from './partnerBookings';
import { getJson, postJson } from './client';

// --- Types (order PartnerKycController / PartnerSettlementController) ---

export type KycStatus = 'NONE' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export type KycStatusResponse = {
  vendorId: string;
  ownerUserId: string;
  kycStatus: KycStatus | string;
  kycSubmittedAt: string | null;
  kycApprovedAt: string | null;
  payoutAccountBankCode: string | null;
  payoutAccountNumber: string | null;
  payoutAccountHolder: string | null;
};

export type SubmitKycRequest = {
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
};

export type PayoutStatus =
  | 'PENDING'
  | 'HELD'
  | 'READY'
  | 'REQUESTED'
  | 'PAID'
  | 'FAILED'
  | 'RECONCILE_MISMATCH';

export type PayoutSummary = {
  id: string;
  reservationId: string | null;
  gradeCode: string | null;
  marginRateBps: number | null;
  paymentAmountWon: number;
  platformFeeWon: number | null;
  vendorPayoutWon: number | null;
  expectedPayoutWon: number | null;
  requestedPayoutWon: number | null;
  confirmedPayoutWon: number | null;
  platformCouponDiscountWon: number | null;
  platformPointUsedWon: number | null;
  adjustmentSummary: string | null;
  status: PayoutStatus | string;
  holdUntilAt: string | null;
  requestedAt: string | null;
  paidAt: string | null;
  createdAt: string;
};

export type PayoutDetail = PayoutSummary & {
  vendorId: string;
  listPriceWon: number | null;
  vendorCouponDiscountWon: number | null;
  marginBaseWon: number | null;
  vendorMarginWon: number | null;
  failureReason: string | null;
};

export type SettlementSummaryResponse = {
  fromKst: string | null;
  toKst: string | null;
  totalPaymentAmountWon: number;
  totalPlatformFeeWon: number;
  totalExpectedPayoutWon: number;
  totalConfirmedPayoutWon: number;
  payoutCount: number;
  pendingPayoutCount: number;
  readyPayoutCount: number;
  paidPayoutCount: number;
  failedPayoutCount: number;
  averageMarginRateBps: number | null;
  byStatusCount: Record<string, number>;
};

export type SettlementQuery = {
  from?: string;
  to?: string;
  status?: Array<PayoutStatus | string>;
  page?: number;
  size?: number;
};

function buildSettlementQuery(params: SettlementQuery) {
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  (params.status ?? []).forEach((status) => query.append('status', String(status)));
  if (params.page != null) query.set('page', String(params.page));
  if (params.size != null) query.set('size', String(params.size));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

// --- Functions ---

export function getMyKycStatus() {
  return getJson<KycStatusResponse>('/api/v1/vendors/me/settlement/kyc');
}

export function submitKyc(req: SubmitKycRequest) {
  return postJson<KycStatusResponse>('/api/v1/vendors/me/settlement/kyc', req);
}

export function getMyPayouts(params: SettlementQuery = {}) {
  return getJson<SpringPage<PayoutSummary>>(
    `/api/v1/vendors/me/settlement/payouts${buildSettlementQuery(params)}`,
  );
}

export function getMyPayoutDetail(payoutId: string) {
  return getJson<PayoutDetail>(
    `/api/v1/vendors/me/settlement/payouts/${encodeURIComponent(payoutId)}`,
  );
}

export function getMySettlementSummary(params: Omit<SettlementQuery, 'page' | 'size'> = {}) {
  return getJson<SettlementSummaryResponse>(
    `/api/v1/vendors/me/settlement/summary${buildSettlementQuery(params)}`,
  );
}
